import { MockCard, MockButton, MockWarning } from '../../components/mockups/MockUI'
import { MockTable } from '../../components/mockups/MockTable'
import { MockBadge } from '../../components/mockups/MockBadge'

export function SuppliersOverview() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Справочник поставщиков
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Раздел «Поставщики» — единый справочник всех поставщиков, с которыми работает закупка:
          условия оплаты, лимиты, рейтинг надёжности, рекламации и статистика работы. Чтение
          доступно всей команде закупки (закупщик, РОЗ) и админ-ролям (ADMIN, Генеральный директор);
          правка — только РОЗ и ADMIN.
        </p>
      </div>
      <MockTable
        caption="Основные колонки таблицы"
        columns={[{ header: 'Колонка' }, { header: 'Что означает' }]}
        rows={[
          [
            'Поставщик',
            'Название; клик открывает карточку. Бейдж «+N» — присоединённые вторые имена (алиасы)',
          ],
          ['Рейтинг', 'Звёзды 1–5, ручная оценка надёжности (ставится в карточке)'],
          ['Квот', 'Сколько квот с ценой дал поставщик — по основному имени и всем алиасам'],
          ['В счёт', 'Сколько позиций ушло в счёт с квотой этого поставщика (его «победы»)'],
          ['Тип', 'Производитель / Дистрибьютор / Агент/трейдер / Площадка'],
          ['Ответственный закупщик', '«Кто чей поставщик»; РОЗ/ADMIN назначают прямо в таблице'],
          ['Условия оплаты', 'Например: 100% предоплата, Предоплата 50/50, Постоплата'],
          ['Лимит', 'Кредитный лимит с валютой (CNY/USD/EUR/RUB)'],
          ['Отсрочка, дн. / Гарантия, дн.', 'Отсрочка после отгрузки и срок гарантии'],
          ['Рекламации', 'Суммарный счётчик: число из файла РОЗ + записи журнала рекламаций'],
        ]}
      />
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <div className="font-medium text-gray-800 dark:text-gray-200">Поиск и фильтры:</div>
          <ul className="list-disc list-inside space-y-1">
            <li>
              Строка «Поиск по названию и алиасам…» ищет и по вторым именам: запрос «Wtron» найдёт
              поставщика WTR, в который «Wtron» был влит при объединении дублей
            </li>
            <li>
              На каждой колонке — фильтр-воронка с чекбоксами: можно оставить, например, только
              «Дистрибьютор» с условиями «100% предоплата»
            </li>
            <li>
              Порядок, ширина и сортировка колонок сохраняются персонально для каждого пользователя
              — перетащите колонки как удобно, вид восстановится при следующем входе
            </li>
          </ul>
        </div>
      </MockCard>
    </div>
  )
}

export function SuppliersCard() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Карточка поставщика
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Клик по имени поставщика в таблице открывает страницу карточки{' '}
          <span className="font-mono text-xs">/suppliers/:id</span> — всё об одном поставщике:
          условия, статистика, сильные стороны и история проблем.
        </p>
      </div>
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-3">
          <div className="font-medium text-gray-800 dark:text-gray-200">Что на странице:</div>
          <ul className="list-disc list-inside space-y-1">
            <li>
              Шапка: имя, рейтинг-звёзды, тип, строка «Он же: …» с алиасами и ответственный
              закупщик. РОЗ/ADMIN ставят оценку кликом по звезде; повторный клик по текущей оценке
              снимает её
            </li>
            <li>
              Условия из справочника: Оплата, Лимит, Отсрочка после отгрузки, Гарантия и Спецусловия
              (например «разъёмы 50/50, ступенчатые лимиты»)
            </li>
            <li>
              KPI работы: «Заявок участвовал», «BOM-запросов», «Позиций на просчёт», «Квот дал», «С
              ценой &gt;0» и «Ушло в счёт» — счётчики собираются по всем именам поставщика
            </li>
            <li>
              «Сильные бренды» и «Сильные сегменты» — чипы в формате победы·квоты: по каким брендам
              и сегментам поставщик чаще всего квотирует и выигрывает
            </li>
            <li>
              «Участие в заявках» — таблица последних заявок с колонками Заявка / Клиент / Создана /
              Квот / В счёт; номер заявки — ссылка на её страницу
            </li>
          </ul>
          <p>
            Кнопки <MockButton variant="secondary">Редактировать</MockButton> и{' '}
            <MockButton variant="danger">Скрыть из справочника</MockButton> видят только РОЗ и
            ADMIN.
          </p>
        </div>
      </MockCard>
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <div className="font-medium text-gray-800 dark:text-gray-200">Журнал рекламаций:</div>
          <p>
            Каждый проблемный случай фиксируется отдельной записью: вид (
            <MockBadge color="red">Контрафакт</MockBadge>{' '}
            <MockBadge color="red">Недогруз</MockBadge> <MockBadge color="red">Качество</MockBadge>{' '}
            <MockBadge color="red">Срыв сроков</MockBadge> <MockBadge color="red">Другое</MockBadge>
            ), описание и опционально № заявки. Запись добавляется кнопкой{' '}
            <MockButton>+ Случай</MockButton> — писать в журнал может любой закупщик, РОЗ и ADMIN.
          </p>
          <p>
            Счётчик «всего» складывает ручное число рекламаций из файла РОЗ и записи журнала — та же
            сумма показывается в колонке «Рекламации» таблицы.
          </p>
        </div>
      </MockCard>
    </div>
  )
}

export function SuppliersCleanup() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Чистка справочника
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Справочник пополняется автоматически из рабочих имён, поэтому в нём накапливаются опечатки
          и мусорные записи. РОЗ/ADMIN убирают их скрытием — кнопка «×» в конце строки таблицы или
          «Скрыть из справочника» в карточке.
        </p>
      </div>
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <div className="font-medium text-gray-800 dark:text-gray-200">
            Скрытие — это безопасно:
          </div>
          <ul className="list-disc list-inside space-y-1">
            <li>
              Квоты, заявки и вся история не страдают — поставщик исчезает только из списка
              справочника
            </li>
            <li>
              Имя остаётся «занятым», поэтому автопополнение из новых квот НЕ воскресит скрытую
              запись — вычищенный мусор не вернётся сам
            </li>
            <li>Перед скрытием система показывает диалог подтверждения с кнопкой «Скрыть»</li>
          </ul>
        </div>
      </MockCard>
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <div className="font-medium text-gray-800 dark:text-gray-200">Как вернуть скрытого:</div>
          <ol className="list-decimal list-inside space-y-1">
            <li>
              Рядом с поиском нажмите <MockButton variant="secondary">Скрытые (N)</MockButton> —
              кнопка видна РОЗ/ADMIN, когда есть хотя бы один скрытый
            </li>
            <li>Откроется режим «Скрытые поставщики» — те же колонки, только скрытые записи</li>
            <li>В строке нужного поставщика нажмите «Вернуть» — он снова в справочнике</li>
            <li>
              Назад — кнопка <MockButton variant="secondary">← К справочнику</MockButton>
            </li>
          </ol>
        </div>
      </MockCard>
    </div>
  )
}

export function SuppliersMerge() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Объединение дублей
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Один поставщик часто заведён под несколькими написаниями (WTR / Wtr / Wtron). Объединение
          склеивает их в одну карточку: статистика и рекламации суммируются, а история не
          переписывается. Доступно РОЗ и ADMIN.
        </p>
      </div>
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-3">
          <div className="font-medium text-gray-800 dark:text-gray-200">Как объединить:</div>
          <ol className="list-decimal list-inside space-y-1">
            <li>Отметьте чекбоксами в таблице 2 и больше строк-дублей</li>
            <li>
              Появится кнопка <MockButton variant="secondary">Объединить (N)</MockButton> — нажмите
              её
            </li>
            <li>
              В окне «Объединить поставщиков» выберите радиокнопкой главного — по умолчанию
              предложен тот, у кого больше квот
            </li>
            <li>
              Нажмите <MockButton>Объединить</MockButton> — остальные станут вторыми именами
              (алиасами) главного
            </li>
          </ol>
        </div>
      </MockCard>
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          <div className="font-medium text-gray-800 dark:text-gray-200">
            Что происходит при объединении:
          </div>
          <ul className="list-disc list-inside space-y-1">
            <li>Счётчики «Квот» и «В счёт» складываются по всем именам</li>
            <li>Записи журнала рекламаций переезжают к главному</li>
            <li>Квоты и заявки не меняются — история остаётся как была</li>
            <li>
              У главного появляется бейдж «+N» с числом алиасов; поиск по старому имени находит
              объединённую карточку
            </li>
          </ul>
        </div>
      </MockCard>
      <MockWarning>
        Имя-алиас защищено от задвоения: попытка снова создать поставщика «Wtron» вручную вернёт
        ошибку 409 — «Имя „Wtron" уже присоединено к поставщику „WTR" как второе имя (алиас)».
        Повторный импорт Excel со старым именем тоже не создаст дубль — условия применятся к
        главному.
      </MockWarning>
    </div>
  )
}

export function SuppliersImport() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Автопополнение и импорт из Excel
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Справочник живёт сам: как только закупщик указывает новое имя поставщика в квоте или
          BOM-запросе, в справочнике появляется карточка-заглушка (только имя). РОЗ/ADMIN затем
          дозаполняют условия и назначают ответственного. Служебные имена («Цена менеджера», «Другой
          поставщик») в справочник не попадают.
        </p>
      </div>
      <MockCard>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-3">
          <div className="font-medium text-gray-800 dark:text-gray-200">
            Импорт файла «Условия/Объём CNY»:
          </div>
          <ol className="list-decimal list-inside space-y-1">
            <li>
              Нажмите <MockButton variant="secondary">Импорт из Excel</MockButton> и выберите файл
              (.xlsx/.xls) — кнопка доступна РОЗ/ADMIN
            </li>
            <li>
              Колонки распознаются по заголовкам первой строки, порядок и лишние колонки не важны:
              «Поставщик» (обязательная), «Тип», «Ответственный», «Условия оплаты», «Лимит»,
              «Валюта», «NET», «Гарант», «Реклам», «Примечание»
            </li>
            <li>
              Ответственный сопоставляется по имени («Ксения», «Валерия») с пользователями-
              закупщиками; ненайденные имена перечислит итоговое сообщение
            </li>
            <li>
              Итог придёт тостом: «Импорт завершён: создано N, обновлено M». Существующие карточки
              обновляются по имени, дубли не создаются
            </li>
          </ol>
          <p>
            Текст условий оплаты из файла автоматически классифицируется в тип (100% предоплата,
            Предоплата 50/50, Предоплата 30/70, Постоплата (кредитная линия), Лимит размещения,
            Смешанные (порог по сумме), Договорные), а полная формулировка сохраняется в
            «Спецусловия» — детали вроде «разъёмы 50/50» не теряются.
          </p>
        </div>
      </MockCard>
      <MockTable
        caption="Кто что может в разделе «Поставщики»"
        columns={[{ header: 'Действие' }, { header: 'Роли' }]}
        rows={[
          [
            'Просмотр справочника и карточек',
            <span key="r">
              <MockBadge color="blue">Закупщик</MockBadge> <MockBadge color="blue">РОЗ</MockBadge>{' '}
              <MockBadge color="purple">ADMIN</MockBadge>{' '}
              <MockBadge color="purple">Ген. директор</MockBadge>
            </span>,
          ],
          [
            'Запись в журнал рекламаций («+ Случай»)',
            <span key="r">
              <MockBadge color="blue">Закупщик</MockBadge> <MockBadge color="blue">РОЗ</MockBadge>{' '}
              <MockBadge color="purple">ADMIN</MockBadge>
            </span>,
          ],
          [
            'Правка карточек, рейтинг, скрытие, объединение, импорт',
            <span key="r">
              <MockBadge color="blue">РОЗ</MockBadge> <MockBadge color="purple">ADMIN</MockBadge>
            </span>,
          ],
        ]}
      />
    </div>
  )
}
